import isEqual from 'react-fast-compare';

import {makeReactContext} from 'lib/makeReactContext';
import {
  FlowchartNode,
  TaskSchemaDomainData,
  TaskSchemaNode,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';

export interface NodesContextValue {
  nodes: Array<FlowchartNode>;
  // startNode may not be present in the nodes array.  It is here to provide
  // information for map from start sections
  startNode: TaskSchemaDomainData | null;
  endNode: TaskSchemaNode | null;
}

/**
 * Custom equality that ignores node position/dragging fields, which change every
 * frame during drag but are not used by context consumers. All other fields
 * (data, selected, type, id, etc.) are still deep-compared.
 */
function nodesContextIsEqual(a: NodesContextValue, b: NodesContextValue): boolean {
  if (a === b) {
    return true;
  }
  if (!isEqual(a.startNode, b.startNode)) {
    return false;
  }
  if (!isEqual(a.endNode, b.endNode)) {
    return false;
  }
  if (a.nodes.length !== b.nodes.length) {
    return false;
  }
  for (let i = 0; i < a.nodes.length; i++) {
    const nodeA = a.nodes[i];
    const nodeB = b.nodes[i];
    if (nodeA === nodeB) {
      continue;
    }
    const {position: _posA, positionAbsolute: _posAbsA, dragging: _dragA, ...restA} = nodeA;
    const {position: _posB, positionAbsolute: _posAbsB, dragging: _dragB, ...restB} = nodeB;
    if (!isEqual(restA, restB)) {
      return false;
    }
  }
  return true;
}

/** Context to provide the full set of nodes along with the start and end node */
export const NodesContext = makeReactContext<NodesContextValue>(
  () => ({
    nodes: [],
    startNode: null,
    endNode: null,
  }),
  'NodesContext',
  nodesContextIsEqual
);
